import Button from "@mui/material/Button";
import { format, addMonths } from "date-fns";

const date = new Date();

interface IMonth {
  label: string;
  value: string;
  year: string;
}

const months = new Array(12).fill(0).map((_, i) => {
  const dt = addMonths(date, i);
  return {
    label: format(dt, "MMM"),
    value: format(dt, "yyyy-MM-dd"),
    year: format(dt, "yyyy"),
  };
});

const groups = months.reduce((acc, month: IMonth) => {
  if (!acc[month.year]) {
    acc[month.year] = [];
  }
  acc[month.year].push(month);
  return acc;
}, {} as Record<string, IMonth[]>);

const toMonthKey = (value: string) => value.slice(0, 7);

const toDateWithDay = (value: string, day: number) => {
  const targetDate = new Date(`${value.slice(0, 7)}-01T00:00:00`);
  targetDate.setDate(day);
  return format(targetDate, "yyyy-MM-dd");
};

const MonthPicker = ({
  value,
  setValue,
  splitMonthKeys = [],
}: {
  value: string;
  setValue: (value: string) => void;
  splitMonthKeys?: string[];
}) => {
  const selectedMonthKey = toMonthKey(value);
  const splitMonthKeySet = new Set(splitMonthKeys);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
        width: 250,
      }}
    >
      {Object.keys(groups).map((year, i) => {
        const isLast = i === Object.keys(groups).length - 1;

        return (
          <div
            key={year}
            style={{
              borderBottom: !isLast ? "1px solid #d9d9d9" : undefined,
            }}
          >
            <div
              style={{
                paddingTop: 10,
                textAlign: "center",
                fontSize: 20,
                fontWeight: 500,
                color: "#878787",
              }}
            >
              {year}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {groups[year].map((month) => {
                const monthKey = toMonthKey(month.value);
                const selected = selectedMonthKey === monthKey;
                const isSplitMonth = splitMonthKeySet.has(monthKey);

                if (isSplitMonth) {
                  const selectedDay = Number(value.slice(8, 10));
                  const isFirstHalfSelected =
                    selected && selectedDay < 16;
                  const isSecondHalfSelected =
                    selected && selectedDay >= 16;

                  return (
                    <div
                      key={`${month.year}-${month.label}`}
                      style={{
                        width: 75,
                        margin: "5px 5px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <div
                        style={{
                          color: "#62748e",
                          fontSize: 22,
                          lineHeight: "22px",
                          height: 22,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {month.label}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          gap: 2,
                        }}
                      >
                        <Button
                          id="monthPickerButton"
                          style={{
                            minWidth: 0,
                            width: "50%",
                            height: 24,
                            padding: 0,
                            borderRadius: "7px 0 0 7px",
                            border: "1px solid #E2E8F0",
                            backgroundColor: isFirstHalfSelected
                              ? "#38BDF8"
                              : "#F1F5F9",
                            color: isFirstHalfSelected ? "#FFFFFF" : "#475569",
                            fontSize: 13,
                            fontWeight: 500,
                            letterSpacing: 0.46,
                            lineHeight: "22px",
                          }}
                          onClick={() => {
                            setValue(toDateWithDay(month.value, 1));
                          }}
                        >
                          1H
                        </Button>
                        <Button
                          id="monthPickerButton"
                          style={{
                            minWidth: 0,
                            width: "50%",
                            height: 24,
                            padding: 0,
                            borderRadius: "0 7px 7px 0",
                            border: "1px solid #E2E8F0",
                            backgroundColor: isSecondHalfSelected
                              ? "#38BDF8"
                              : "#F1F5F9",
                            color: isSecondHalfSelected ? "#FFFFFF" : "#475569",
                            fontSize: 13,
                            fontWeight: 500,
                            letterSpacing: 0.46,
                            lineHeight: "22px",
                          }}
                          onClick={() => {
                            setValue(toDateWithDay(month.value, 16));
                          }}
                        >
                          2H
                        </Button>
                      </div>
                    </div>
                  );
                }

                return (
                  <Button
                    key={`${month.year}-${month.label}`}
                    id="monthPickerButton"
                    style={{ padding: "10px 20px", margin: "5px 5px" }}
                    color={selected ? "secondary" : "neutral"}
                    size="medium"
                    variant={selected ? "contained" : "text"}
                    onClick={() => {
                      setValue(month.value);
                    }}
                  >
                    {month.label}
                  </Button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MonthPicker;
